<script setup lang="ts">
/**
 * Brand-styled native range slider.
 *
 * Why not v-slider: Vuetify's slider tracks pointers in JS, which means
 * iOS Safari can cancel the gesture mid-drag (any vertical drift, any
 * scroll-vs-drag arbitration) and the value rolls back. `<input
 * type="range">` is owned by the browser — touch handling is identical
 * to every native iOS slider, and a release ALWAYS commits the value.
 *
 * API mirrors v-slider closely (modelValue / min / max / step / color)
 * so it's a drop-in replacement.
 */
import { computed } from 'vue';

interface Props {
  modelValue: number;
  min?:   number;
  max?:   number;
  step?:  number;
  /** Theme token ('primary' / 'secondary') or any CSS color string. */
  color?: string;
  ariaLabel?: string;
  disabled?:  boolean;
}

const props = withDefaults(defineProps<Props>(), {
  min:   0,
  max:   100,
  step:  1,
  color: 'primary',
  disabled: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

const NAMED_COLORS: Record<string, string> = {
  primary:   '#1FA8F2',
  secondary: '#3BC4B5',
  success:   '#10B981',
  warning:   '#F59E0B',
  error:     '#EF4444',
};

const resolvedColor = computed(() => NAMED_COLORS[props.color] ?? props.color);

const fillPct = computed(() => {
  const span = props.max - props.min;
  if (span <= 0) return 0;
  const v = (props.modelValue - props.min) / span;
  return Math.max(0, Math.min(1, v)) * 100;
});

function onInput(e: Event) {
  const v = Number((e.target as HTMLInputElement).value);
  if (!Number.isNaN(v)) emit('update:modelValue', v);
}
</script>

<template>
  <input
    class="lockin-slider"
    type="range"
    :value="modelValue"
    :min="min"
    :max="max"
    :step="step"
    :disabled="disabled"
    :aria-label="ariaLabel"
    :style="{
      '--lockin-slider-pct':   fillPct + '%',
      '--lockin-slider-color': resolvedColor,
    }"
    @input="onInput"
  />
</template>

<style scoped>
.lockin-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 36px;        /* generous touch target — track is 6px, thumb 24px */
  background: transparent;
  display: block;
  margin: 0;
  padding: 0;
  cursor: pointer;
  /* Reset Safari's default font-size leak that adds vertical spacing. */
  font-size: 0;
}
.lockin-slider:focus {
  outline: none;
}
.lockin-slider:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ─── WebKit (Safari, Chrome, all iOS browsers) ─── */
.lockin-slider::-webkit-slider-runnable-track {
  height: 6px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(
    to right,
    var(--lockin-slider-color) 0%,
    var(--lockin-slider-color) var(--lockin-slider-pct),
    rgba(0, 0, 0, 0.12)        var(--lockin-slider-pct),
    rgba(0, 0, 0, 0.12)        100%
  );
}
.lockin-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width:  24px;
  height: 24px;
  border-radius: 50%;
  background: var(--lockin-slider-color);
  border: 2px solid #ffffff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.18);
  margin-top: -9px;    /* (thumb 24 - track 6) / 2, then -3 to compensate border */
  cursor: pointer;
  transition: transform 0.12s ease;
}
.lockin-slider:active::-webkit-slider-thumb {
  transform: scale(1.18);
}

/* ─── Firefox ─── */
.lockin-slider::-moz-range-track {
  height: 6px;
  border-radius: 999px;
  border: none;
  background: rgba(0, 0, 0, 0.12);
}
.lockin-slider::-moz-range-progress {
  height: 6px;
  border-radius: 999px;
  background: var(--lockin-slider-color);
}
.lockin-slider::-moz-range-thumb {
  width:  24px;
  height: 24px;
  border-radius: 50%;
  background: var(--lockin-slider-color);
  border: 2px solid #ffffff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.18);
  cursor: pointer;
}
</style>
